import { NavLink } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { NavigationCategory as NavigationCategoryType, NavigationItem, User } from '@/types/navigation'

interface NavigationCategoryProps {
  category: NavigationCategoryType
  user: User | null
  onClose?: () => void
  isCollapsed?: boolean
  onToggleCollapse?: (categoryId: string) => void
}

const NavigationCategory = ({
  category,
  user,
  onClose,
  isCollapsed = category.collapsedByDefault,
  onToggleCollapse
}: NavigationCategoryProps) => {
  const isOpen = !isCollapsed

  const handleToggle = () => {
    if (onToggleCollapse) {
      onToggleCollapse(category.id)
    }
  }

  // Check if user can see this category based on role
  const canSeeCategory = () => {
    if (!user) return false
    return category.roles.includes(user.role)
  }

  // Filter items by user role and permissions
  const filterItemsByUser = (items: NavigationItem[]): NavigationItem[] => {
    if (!user) return []

    return items.filter(item => {
      // Check role
      if (!item.roles.includes(user.role)) return false

      // For merchant users, check permissions
      if (user.role === 'merchant' && user.merchantContext) {
        const { permissionLevel, permissions } = user.merchantContext

        // Check permission level hierarchy
        const permissionHierarchy = ['viewer', 'manager', 'admin', 'owner']
        if (item.requiredPermission) {
          const requiredIndex = permissionHierarchy.indexOf(item.requiredPermission)
          const userIndex = permissionHierarchy.indexOf(permissionLevel)
          if (userIndex < requiredIndex) return false
        }

        // Check specific permission if required
        if (item.requiredSpecificPermission) {
          const permissionKey = item.requiredSpecificPermission as keyof typeof permissions
          return permissions[permissionKey] || false
        }
      }

      return true
    })
  }

  if (!canSeeCategory()) {
    return null
  }

  const filteredItems = filterItemsByUser(category.items)
  if (filteredItems.length === 0) {
    return null
  }

  const Icon = category.icon

  return (
    <div className="mb-2">
      {/* Category header */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-150"
        aria-expanded={isOpen}
        aria-controls={`category-${category.id}-items`}
        aria-label={`${category.name} menu, ${isOpen ? 'expanded' : 'collapsed'}`}
      >
        <div className="flex items-center">
          <Icon className="mr-3 h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span className="truncate">{category.name}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        )}
      </button>

      {/* Category items */}
      <div
        id={`category-${category.id}-items`}
        className={`mt-1 space-y-1 ${isOpen ? 'block' : 'hidden'}`}
        role="region"
        aria-label={`${category.name} menu`}
      >
        {filteredItems.map((item) => {
          const ItemIcon = item.icon
          return (
            <NavLink
              key={`${category.id}-${item.href}`}
              to={item.href}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center px-3 py-2 text-sm rounded-lg ml-8 ${
                  isActive
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }
              end
            >
              <ItemIcon className="mr-3 h-4 w-4" aria-hidden="true" />
              <span className="flex-1">{item.name}</span>
              {item.badge && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  {item.badge}
                </span>
              )}
              {item.isNew && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Nouveau
                </span>
              )}
            </NavLink>
          )
        })}
      </div>
    </div>
  )
}

export default NavigationCategory